/**
 * Photos are downscaled on the device before they are stored.
 *
 * A modern phone camera produces 4–8 MB per shot. Queued offline in IndexedDB,
 * a dozen of those is a quota error; pushed over a factory's uplink, it is a
 * sync that never finishes. 1280px at JPEG 0.72 is still far more detail than
 * anyone needs to recognise a mould, at roughly 150 KB.
 */
const MAX_EDGE = 1280;
const QUALITY = 0.72;

export async function fileToDataUrl(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);

    // JPEG, not PNG: these are photographs, and PNG would be several times larger
    // for no visible gain.
    return canvas.toDataURL('image/jpeg', QUALITY);
  } finally {
    bitmap.close();
  }
}

/** Rough byte size of a data URL, for showing the user what they just attached. */
export function dataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return 0;
  const base64 = dataUrl.slice(comma + 1);
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

/**
 * The same downscale, as bytes to upload rather than text to store. A picture
 * that cannot be decoded here (an unusual format) is sent as it is, and the
 * server - which checks content, not names - says whether it can be kept.
 */
export async function fileToJpegBlob(file: File): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    // Never send something larger than what was chosen.
    return blob && blob.size < file.size ? blob : file;
  } finally {
    bitmap.close();
  }
}
